import * as PropTypes from "prop-types";
import { connect } from 'react-redux';
import {ff, form, Loader} from "@mc/mc-legacy";
import {Component} from "react";
import * as React from "react";
import { Alert, Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
const { Form, FormActions, FormBody } = form;
import { formValueSelector, reduxForm } from "redux-form";
import FileUploadField from "./fields/FileUploadField";
import ObjectSelect from "../components/ObjectSelect";
import ObjednavkaCustomApi from "../api/ObjednavkaCustomApi";
import CustomCheckboxField from "../components/CustomCheckboxField";
import { getFilePath } from "../utils/pathFormatter";
import {withTranslation, WithTranslation} from "react-i18next";

export const FIELD_PREFIX_UKON = "ukon_";

/**
 * Formulář zakázky.
 */
class ZakazkaForm extends Component<WithTranslation> {

    static formName = 'ZakazkaForm';

    static FIELD_SOUBORY = "fileMetas";

    static propTypes = {
        souborAtributyList: PropTypes.array.isRequired, // seznam souborových atributů
        ukonyList: PropTypes.array.isRequired, // seznam úkonů
    };

    state = {
        uploading: false,
    };

    static validate = (values, props) => {
        const {t} = props;
        const errors = {};
        if (!values.pocetVyhotoveni) {
            errors.pocetVyhotoveni = t('validation.field_required');
        }
        if (values.razitkovat && !values.ciselnaRada) {
            errors.ciselnaRada = t('validation.stamping_number_series_required');
        }
        return errors;
    };

    componentDidUpdate(prevProps) {
        // TODO hack kvůli hovnokódovému FileUpload fieldu - sám se po invalidate dat nevymaže
        if (prevProps.initialValues && this.props.initialValues && prevProps.initialValues[ZakazkaForm.FIELD_SOUBORY] && !this.props.initialValues[ZakazkaForm.FIELD_SOUBORY]) {
            this.props.change(ZakazkaForm.FIELD_SOUBORY, null);
        }
    }

    /**
     * Vrací promise. Provede aktualizaci souborů při přidání nových/smazání existujících.
     * Nové soubory nahraje na server a uloží jejich dočasné kódy.
     *
     * @param files soubory
     * @returns {Promise<any>}
     */
    updateFiles = (files) => {
        return new Promise(resolve => {
            const { souborAtributyList, fileMetas } = this.props;
            // Z nějakého důvodu sem soubory dojdou jako objekt místo pole
            const fileArray = Object.keys(files).filter(k => !isNaN(k)).map(k => files[k]);

            const newFiles = fileArray && fileMetas
                ? fileArray.filter(f => !fileMetas.find(meta => {
                    const metaPath = meta.podslozky ? `${meta.podslozky}${meta.name}` : meta.fullPath || meta.webkitRelativePath;
                    const filePath = f.podslozky ? `${f.podslozky}${f.name}` : f.fullPath || f.webkitRelativePath;
                    return metaPath === filePath;
                }))
                : fileArray;
            const removedFiles = fileArray && fileMetas
                ? fileMetas.filter(meta => !fileArray.find(f => f.name === meta.name && f.podslozky === meta.podslozky)) : [];

            let result = fileMetas || [];

            // Odstranění smazaných souborů
            if (removedFiles.length > 0) {
                result = result.filter(meta => !removedFiles.find(f => f.name === meta.name && f.podslozky === meta.podslozky));
            }

            const droppedFolder = newFiles.reduce((folder, f) => {
                if (f.fullPath) {
                    folder.push(f.fileObject);
                }

                return folder && folder.length > 0 ? folder : null;
            }, []);

            // Přidání nových souborů
            if (newFiles.length > 0) {
                const newFilesMetadata = [];
                this.setState({ uploading: true });

                ObjednavkaCustomApi.createTempSoubory(droppedFolder || newFiles).then(fileCodesList => {
                    for (let i = 0; i < newFiles.length; i++) {
                        const { name, fullPath, webkitRelativePath } = newFiles[i];

                        const attributes = {};
                        const path = fullPath || webkitRelativePath;
                        const podslozky = getFilePath(path);
                        const code = fileCodesList[i];

                        const metadata = {attributes, name, code, podslozky};

                        for (let atribut of souborAtributyList) {
                            if (atribut.hodnoty.length > 0) {
                                metadata.attributes[atribut.id] = atribut.hodnoty[0];
                            }
                        }
                        newFilesMetadata.push(metadata);
                    }
                    result = [...result, ...newFilesMetadata];
                    this.setState({ uploading: false });
                    resolve(result);
                });
            } else {
                resolve(result);
            }
        });
    };

    handleFilesChange = (files) => {
        this.updateFiles(files).then(newMetadata => {
            this.props.change(ZakazkaForm.FIELD_SOUBORY, newMetadata);
        })
    };

    render() {
        const { onClose, handleSubmit, submitting, ukonyList, error, razitkovat, t} = this.props;
        return <Form onSubmit={handleSubmit} horizontal>
            {error && <Alert color="danger mb-3">{error}</Alert>}
            <FormBody>
                {!submitting ?
                    <Row>
                        <Col xs={12} md={8}>
                            <FileUploadField uploading={this.state.uploading} name={ZakazkaForm.FIELD_SOUBORY} onChange={this.handleFilesChange} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Card>
                                <CardHeader>
                                    <h2>{t('order.specification')}</h2>
                                </CardHeader>
                                <CardBody>
                                    <ff.Number int label={t('order.number_of_copies')} name="pocetVyhotoveni" disabled={submitting} required addonsAfter={<Col xs={10} />} />
                                    {ukonyList.map(ukon =>
                                        <ObjectSelect
                                            key={`${FIELD_PREFIX_UKON}${ukon.id}`}
                                            label={ukon.nazevUkon}
                                            name={`${FIELD_PREFIX_UKON}${ukon.id}`}
                                            items={ukon.polozky && ukon.polozky.filter(u => !u.neaktivni)}
                                            itemValueAttrName="popisUkon"
                                            showEmpty={!ukon.povinne}
                                            disabled={submitting}
                                            required={ukon.povinne}
                                        />
                                    )}
                                    <CustomCheckboxField label={t('order.stamping')} name="razitkovat" disabled={submitting} />
                                    <ff.String label={t('order.number_series')} name="ciselnaRada" disabled={submitting || !razitkovat} maxLength={20} />
                                    <ff.Textarea label={t('note')} name="poznamka" disabled={submitting} />
                                    <FormActions onClose={onClose} disabled={submitting} />
                                </CardBody>
                            </Card>
                            <div className="submit-button-panel">
                                <Button color="primary" className="mb-5">{t('order.next_step')}</Button>
                            </div>
                        </Col>
                    </Row>
                    :
                    <Row>
                        <Col xs={12}>
                            <Loader>{t('please_wait_uploading')}</Loader>
                        </Col>
                    </Row>}
            </FormBody>
        </Form>
    }
}

const selector = formValueSelector(ZakazkaForm.formName);

const mapStateToProps = state => ({
    fileMetas: selector(state, ZakazkaForm.FIELD_SOUBORY),
    razitkovat: selector(state, "razitkovat"),
});

export default withTranslation()(connect(mapStateToProps)(reduxForm({
    form: ZakazkaForm.formName,
    validate: ZakazkaForm.validate,
})(ZakazkaForm)));
